import React from 'react';
import styled from 'styled-components';
import PageHero from '../components/PageHero';
import { FiAward, FiUsers, FiTarget, FiTrendingUp, FiHeart, FiStar, FiMonitor, FiBookOpen } from 'react-icons/fi';
import ScrollAnimation from '../components/ScrollAnimation';

const AboutContainer = styled.div`
  margin-top: 0;
  overflow-x: hidden;
  width: 100%;
`;

// Replaced by PageHero

const Container = styled.div`
  max-width: ${props => props.theme.breakpoints.wide};
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
  width: 100%;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 ${props => props.theme.spacing.md};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    /* Ensure comfortable side gutters on small screens */
    padding: 0 ${props => props.theme.spacing.md};
  }
`;

// PageHero handles heading/subtitle styling

const Section = styled.section`
  padding: ${props => props.theme.spacing['2xl']} 0 ${props => props.theme.spacing['4xl']};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing.xl} 0 ${props => props.theme.spacing['3xl']};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing['2xl']} 0;
  }

  &.gray-bg {
    background: ${props => props.theme.colors.gray[100]};
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing['3xl']};

  h2 {
    font-size: ${props => props.theme.fontSizes['4xl']};
    color: ${props => props.theme.colors.dark};
    margin-bottom: ${props => props.theme.spacing.lg};

    .highlight {
      color: ${props => props.theme.colors.primary};
    }

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      font-size: ${props => props.theme.fontSizes['3xl']};
    }

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: ${props => props.theme.fontSizes['2xl']};
    }
  }

  p {
    /* Scale down a touch on narrow screens for better readability */
    font-size: clamp(${props => props.theme.fontSizes.base}, 3.5vw, ${props => props.theme.fontSizes.xl});
    color: ${props => props.theme.colors.gray[600]};
    max-width: 600px;
    margin: 0 auto;

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: ${props => props.theme.fontSizes.base};
    }
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing['4xl']};
  align-items: center;

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing['2xl']};
    text-align: center;
  }
`;

const TextContent = styled.div`
  h3 {
    font-size: ${props => props.theme.fontSizes['2xl']};
    color: ${props => props.theme.colors.dark};
    margin-bottom: ${props => props.theme.spacing.lg};
  }

  p {
    color: ${props => props.theme.colors.gray[600]};
    line-height: 1.8;
    margin-bottom: ${props => props.theme.spacing['2xl']};
    font-size: ${props => props.theme.fontSizes.base};
    text-align: justify;

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      text-align: left;
      font-size: ${props => props.theme.fontSizes.sm};
      line-height: 1.7;
      margin-bottom: ${props => props.theme.spacing.xl};
    }
  }

  ul {
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: ${props => props.theme.spacing.md};

    li {
      display: flex;
      align-items: center;
      gap: ${props => props.theme.spacing.md};
      color: ${props => props.theme.colors.gray[700]};
      font-size: ${props => props.theme.fontSizes.base};
      padding: ${props => props.theme.spacing.sm} 0;

      @media (max-width: ${props => props.theme.breakpoints.mobile}) {
        font-size: ${props => props.theme.fontSizes.sm};
        gap: ${props => props.theme.spacing.sm};
      }

      svg {
        color: ${props => props.theme.colors.primary};
        flex-shrink: 0;
        width: 20px;
        height: 20px;

        @media (max-width: ${props => props.theme.breakpoints.mobile}) {
          width: 18px;
          height: 18px;
        }
      }
    }
  }
`;

const ImagePlaceholder = styled.div`
  aspect-ratio: 4/3;
  background: ${props => props.theme.colors.gradient.primary};
  border-radius: ${props => props.theme.borderRadius.xl};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.light};
  font-size: ${props => props.theme.fontSizes['3xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.xl};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    animation: pulse 3s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.1); opacity: 0.8; }
  }
  
  .placeholder-icon {
    font-size: 80px;
    margin-bottom: ${props => props.theme.spacing.md};
    opacity: 0.9;
  }
  
  .placeholder-text {
    font-size: ${props => props.theme.fontSizes.xl};
    opacity: 0.95;
    z-index: 1;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => props.theme.spacing['2xl']};
  align-items: stretch;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    /* Use two fluid columns on mobile without min widths to prevent overflow */
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${props => props.theme.spacing.lg};
  }
`;

const StatCard = styled.div`
  background: ${props => props.theme.colors.light};
  padding: ${props => props.theme.spacing['2xl']};
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: ${props => props.theme.shadows.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  text-align: center;
  transition: ${props => props.theme.transitions.default};
  position: relative;
  overflow: hidden;
  height: 100%;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.lg};
    min-height: 160px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.theme.colors.gradient.primary};
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props => props.theme.shadows['2xl']};
    border-color: ${props => props.theme.colors.primary}33;
  }

  &:hover::before {
    opacity: 1;
  }

  .icon {
    width: 56px;
    height: 56px;
    background: ${props => props.theme.colors.gradient.primary};
    border-radius: ${props => props.theme.borderRadius.full};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto ${props => props.theme.spacing.lg};
    color: ${props => props.theme.colors.light};
    font-size: ${props => props.theme.fontSizes['2xl']};
    box-shadow: ${props => props.theme.shadows.md};
    transition: ${props => props.theme.transitions.default};
  }

  &:hover .icon {
    transform: scale(1.1);
    box-shadow: ${props => props.theme.shadows.lg};
  }

  .number {
    font-size: clamp(${props => props.theme.fontSizes.lg}, 4.8vw, ${props => props.theme.fontSizes['3xl']});
    font-weight: ${props => props.theme.fontWeights.bold};
    color: ${props => props.theme.colors.primary};
    margin-bottom: ${props => props.theme.spacing.sm};
    transition: ${props => props.theme.transitions.default};
  }

  &:hover .number {
    transform: scale(1.05);
  }

  .label {
    color: ${props => props.theme.colors.gray[600]};
    font-size: ${props => props.theme.fontSizes.base};
  }`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${props => props.theme.spacing['2xl']};
  align-items: stretch;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.div`
  background: ${props => props.theme.colors.light};
  padding: ${props => props.theme.spacing['2xl']};
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: ${props => props.theme.shadows.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  text-align: center;
  transition: ${props => props.theme.transitions.default};
  position: relative;
  overflow: hidden;
  height: 100%;
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    min-height: 260px;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.theme.colors.gradient.primary};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props => props.theme.shadows['2xl']};
    border-color: ${props => props.theme.colors.primary}33;
  }

  &:hover::after {
    transform: scaleX(1);
  }

  .icon {
    width: 70px;
    height: 70px;
    background: ${props => props.theme.colors.gradient.primary};
    border-radius: ${props => props.theme.borderRadius.full};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto ${props => props.theme.spacing.lg};
    color: ${props => props.theme.colors.light};
    font-size: ${props => props.theme.fontSizes['2xl']};
    box-shadow: ${props => props.theme.shadows.md};
    transition: ${props => props.theme.transitions.default};
  }

  &:hover .icon {
    transform: scale(1.1) rotate(-5deg);
    box-shadow: ${props => props.theme.shadows.lg};
  }

  h3 {
    font-size: ${props => props.theme.fontSizes.xl};
    color: ${props => props.theme.colors.dark};
    margin-bottom: ${props => props.theme.spacing.md};
    font-weight: ${props => props.theme.fontWeights.bold};
  }

  p {
    color: ${props => props.theme.colors.gray[600]};
    line-height: 1.6;
  }
`;

// The following variants remove inline styles and use theme spacing consistently
const MissionVisionCard = styled.div`
  background: ${props => props.theme.colors.light};
  padding: ${props => props.theme.spacing['3xl']};
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: ${props => props.theme.shadows.lg};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  text-align: center;
  transition: ${props => props.theme.transitions.default};
  position: relative;
  overflow: hidden;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: ${props => props.theme.spacing['2xl']};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.xl};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: ${props => props.theme.colors.gradient.primary};
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
  }
  
  &:hover::before {
    transform: scaleX(1);
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${props => props.theme.shadows['2xl']};
    border-color: ${props => props.theme.colors.primary}33;
  }
  
  .icon {
    width: 80px;
    height: 80px;
    background: ${props => props.theme.colors.gradient.primary};
    border-radius: ${props => props.theme.borderRadius.full};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto ${props => props.theme.spacing.xl};
    color: ${props => props.theme.colors.light};
    font-size: 40px;
    box-shadow: ${props => props.theme.shadows.lg};
    transition: ${props => props.theme.transitions.default};

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      width: 60px;
      height: 60px;
      font-size: 30px;
      margin-bottom: ${props => props.theme.spacing.lg};
    }
  }
  
  &:hover .icon {
    transform: scale(1.1) rotate(5deg);
  }
  
  h3 {
    font-size: ${props => props.theme.fontSizes['2xl']};
    color: ${props => props.theme.colors.dark};
    margin-bottom: ${props => props.theme.spacing.lg};
    font-weight: ${props => props.theme.fontWeights.bold};

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: ${props => props.theme.fontSizes.xl};
      margin-bottom: ${props => props.theme.spacing.md};
    }
  }
  
  p {
    color: ${props => props.theme.colors.gray[600]};
    line-height: 1.8;
    font-size: ${props => props.theme.fontSizes.lg};
    max-width: 600px;
    margin: 0 auto;

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      font-size: ${props => props.theme.fontSizes.base};
      line-height: 1.6;
    }
  }
`;

const MissionVisionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${props => props.theme.spacing['3xl']};
  
  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing['2xl']};
  }
`;

const About = () => {
  const stats = [
    { icon: <FiUsers />, number: '5000+', label: 'Students Trained' },
    { icon: <FiAward />, number: '95%', label: 'Job Placement Rate' },
    { icon: <FiTrendingUp />, number: '8+', label: 'Years Experience' },
    { icon: <FiStar />, number: '4.9/5', label: 'Student Rating' }
  ];

  const values = [
    {
      icon: <FiTarget />,
      title: 'Excellence in Education',
      description: 'We are committed to providing the highest quality education with industry-relevant curriculum and expert instructors.'
    },
    {
      icon: <FiHeart />,
      title: 'Student-Centric Approach',
      description: 'Every student is unique. We provide personalized learning experiences tailored to individual goals and learning styles.'
    },
    {
      icon: <FiTrendingUp />,
      title: 'Innovation & Growth',
      description: 'We continuously evolve our programs to stay ahead of industry trends and emerging technologies.'
    }
  ];

  return (
    <AboutContainer>
      <PageHero 
        title="About Service Hub IT Solutions"
        subtitle="Empowering the next generation of tech professionals through comprehensive, industry-focused education and hands-on training programs."
        illustrationSrc="/aboutus.png"
        illustrationAlt="About Service Hub illustration"
        rightHeight="360px"
        rightHeightTablet="300px"
        rightHeightMobile="240px"
        topPaddingTablet="1.25rem"
        topPaddingMobile="2rem"
        compact
      />

      <Section>
        <Container>
          <ScrollAnimation animation="fadeInUp">
            <SectionHeader>
              <h2>Our <span className="highlight">Story</span></h2>
            </SectionHeader>
          </ScrollAnimation>
          <ContentGrid>
            <ScrollAnimation animation="fadeInUp">
              <TextContent>
              <p>
                Servicehub IT Solutions is a dynamic training and consultancy firm committed to transforming businesses and individuals through 
                cutting-edge IT expertise. With a passion for innovation and a focus on practical outcomes, we specialize in delivering high-impact
                 training programs and strategic consulting services tailored to meet the evolving demands of the digital age.
              </p>
              <ul>
                <li><FiAward /> Industry-recognized certifications</li>
                <li><FiUsers /> Experienced faculty from top tech companies</li>
                <li><FiTarget /> Hands-on project-based learning</li>
                <li><FiTrendingUp /> 95% job placement rate</li>
              </ul>
            </TextContent>
            </ScrollAnimation>
            <ScrollAnimation animation="fadeInUp" delay={200}>
              <ImagePlaceholder>
                <div className="placeholder-icon">
                  <FiMonitor />
                </div>
                <div className="placeholder-text">Our Learning Environment</div>
              </ImagePlaceholder>
            </ScrollAnimation>
          </ContentGrid>
        </Container>
      </Section>

      <Section className="gray-bg">
        <Container>
          <ScrollAnimation animation="fadeInUp">
            <SectionHeader>
              <h2>Our <span className="highlight">Impact</span></h2>
              <p>Numbers that reflect our commitment to excellence</p>
            </SectionHeader>
          </ScrollAnimation>
          <StatsGrid>
            {stats.map((stat, index) => (
              <ScrollAnimation key={index} animation="fadeInUp" delay={index * 100}>
                <StatCard>
                  <div className="icon">
                    {stat.icon}
                  </div>
                  <div className="number">{stat.number}</div>
                  <div className="label">{stat.label}</div>
                </StatCard>
              </ScrollAnimation>
            ))}
          </StatsGrid>
        </Container>
      </Section>

      <Section>
        <Container>
          <ScrollAnimation animation="fadeInUp">
            <SectionHeader>
              <h2>Our Mission <span className="highlight">&</span> Vision</h2>
              <p>Guiding principles that drive our every decision</p>
            </SectionHeader>
          </ScrollAnimation>
          <MissionVisionGrid>
            <ScrollAnimation animation="fadeInUp">
              <MissionVisionCard>
                <div className="icon">
                  <FiTarget />
                </div>
                <h3>Our Mission</h3>
                <p>
                  To democratize access to high-quality technology education and empower 
                  individuals with the skills needed to thrive in the digital economy. 
                  We believe that everyone deserves the opportunity to build a successful 
                  career in technology, regardless of their background.
                </p>
              </MissionVisionCard>
            </ScrollAnimation>
            <ScrollAnimation animation="fadeInUp" delay={200}>
              <MissionVisionCard>
                <div className="icon">
                  <FiBookOpen />
                </div>
                <h3>Our Vision</h3>
                <p>
                  To be the leading technology education institute globally, known for 
                  producing industry-ready professionals who drive innovation and positive 
                  change in the world. We envision a future where technology education is 
                  accessible, practical, and transformative for all learners.
                </p>
              </MissionVisionCard>
            </ScrollAnimation>
          </MissionVisionGrid>
        </Container>
      </Section>

      <Section className="gray-bg">
        <Container>
          <ScrollAnimation animation="fadeInUp">
            <SectionHeader>
              <h2>Our <span className="highlight">Values</span></h2>
              <p>The principles that guide everything we do</p>
            </SectionHeader>
          </ScrollAnimation>
          <ValuesGrid>
            {values.map((value, index) => (
              <ScrollAnimation key={index} animation="fadeInUp" delay={index * 100}>
                <ValueCard>
                  <div className="icon">
                    {value.icon}
                  </div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </ValueCard>
              </ScrollAnimation>
            ))}
          </ValuesGrid>
        </Container>
      </Section>
    </AboutContainer>
  );
};

export default About;